import { Modal } from "bootstrap"
import { DirectUpload, DirectUploadDelegate } from "@rails/activestorage"

export function mod(n: number, modulus: number): number {
  return ((n % modulus) + modulus) % modulus
}

export const breakpoints = {
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400,
}

export function blobToFile(blob: Blob, fileName: string | undefined = undefined): File {
  const extensionByMimeType = {
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/gif": "gif",
    "image/svg+xml": "svg",
    "image/webp": "webp",
    "audio/ogg": "ogg",
  }

  if (!fileName) {
    fileName = "blob"

    if (extensionByMimeType[blob.type]) {
      fileName += `.${extensionByMimeType[blob.type]}`
    }
  }

  return new File([blob], fileName, { type: blob.type })
}

let loadingOverlayTimer: number | null = null
export const loadingOverlay = {
  show: (delay: number = 0) => {
    loadingOverlayTimer = window.setTimeout(() => {
      const loadingOverlay = document.querySelector("dialog#loading-overlay") as HTMLDialogElement
      if (loadingOverlay) {
        loadingOverlay.showModal()
      }
    }, delay)
  },
  hide: () => {
    clearTimeout(loadingOverlayTimer)
    loadingOverlayTimer = null
    const loadingOverlay = document.querySelector("dialog#loading-overlay") as HTMLDialogElement
    if (loadingOverlay) {
      loadingOverlay.close()
    }
  },
}

export function openModal(modalEl: string | Element): Modal {
  const modal = Modal.getOrCreateInstance(modalEl)
  modal.show()
  return modal
}

export function openModalFromTemplate(modalHtml: string) {
  const template = document.createElement("template")
  template.innerHTML = modalHtml

  if (template.content.childElementCount != 1) {
    throw new Error("Modal must have exactly one root element")
  }

  const modalEl = template.content.firstElementChild
  modalEl.addEventListener("hidden.bs.modal", () => modalEl.remove())
  document.body.appendChild(modalEl)

  Modal.getOrCreateInstance(modalEl).show()

  return modalEl
}

export function onVisible(element: Element, callback: () => void) {
  const observer = new ResizeObserver((entries) => {
    for (const entry of entries) {
      if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
        callback()
        observer.disconnect()
      }
    }
  })
  observer.observe(element)
}

export function directUpload(file: File, purpose: string) {
  const directUploadsUrl = new URL(
    (document.getElementsByName("direct-uploads-url")[0] as HTMLMetaElement).content,
  )
  directUploadsUrl.searchParams.set("purpose", purpose)
  directUploadsUrl.searchParams.set("locale", document.documentElement.lang)

  return new Promise<{ signed_id: string }>((resolve, reject) => {
    const delegate: DirectUploadDelegate & { metaXHR: XMLHttpRequest; dataXHR: XMLHttpRequest } = {
      metaXHR: null,
      dataXHR: null,
      directUploadWillCreateBlobWithXHR: function (xhr: XMLHttpRequest) {
        this.metaXHR = xhr
      },
      directUploadWillStoreFileWithXHR: function (xhr: XMLHttpRequest) {
        this.dataXHR = xhr
      },
    }

    new DirectUpload(file, directUploadsUrl.toString(), delegate).create((error, uploadResult) => {
      if (error) {
        const result = {
          status: 0,
          error: delegate.dataXHR?.response || delegate.metaXHR?.response || error,
        }

        if (
          delegate.dataXHR?.status &&
          (delegate.dataXHR.status < 200 || delegate.dataXHR.status >= 300)
        ) {
          result.status = delegate.dataXHR.status
        } else if (
          delegate.metaXHR?.status &&
          (delegate.metaXHR.status < 200 || delegate.metaXHR.status >= 300)
        ) {
          result.status = delegate.metaXHR.status
        }

        reject(result)
      } else {
        resolve(uploadResult)
      }
    })
  })
}

export function onDocumentReady(fn: Function) {
  if (document.readyState !== "loading") {
    fn()
  } else {
    document.addEventListener("DOMContentLoaded", fn as EventListener, { once: true })
  }
}
